// RouteTransition — slide-in animation on every route change.
//
// On mobile / app mode, animates the wrapper on every path change by
// toggling a CSS class. The DOM tree below is *not* remounted — that
// would tear down layouts (header, bottom nav, providers) and we'd lose
// state on every navigation. Instead we drop the class for one frame
// and re-apply it, which re-triggers the keyframe on the same element.
//
// Off when:
//   - desktop / mouse-only (useAppMode().isAppMode === false)
//   - prefers-reduced-motion is reduce
//   - first paint (don't animate cold load)

import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useAppMode } from '@/hooks/useAppMode';

interface RouteTransitionProps {
  children: ReactNode;
}

function reducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function RouteTransition({ children }: RouteTransitionProps) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { isAppMode } = useAppMode();
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const firstPaint = useRef(true);

  useEffect(() => {
    if (firstPaint.current) {
      firstPaint.current = false;
      return;
    }
    if (!isAppMode || reducedMotion()) return;

    setDirection(navigationType === 'POP' ? 'back' : 'forward');
    // Drop the class for one frame, then re-apply — this restarts the
    // CSS keyframe without remounting the children.
    setAnimating(false);
    const id = requestAnimationFrame(() => setAnimating(true));
    return () => cancelAnimationFrame(id);
  }, [location.pathname, location.search, navigationType, isAppMode]);

  // Clear the class once the keyframe finishes, otherwise the `both` fill
  // mode leaves `transform: translateX(0)` on the wrapper and Webkit/Blink
  // treat any non-none transform as the containing block for descendant
  // `position: fixed` elements (BottomNav stops sticking to the viewport).
  return (
    <div
      data-direction={direction}
      className={animating ? 'route-transition-enter' : undefined}
      onAnimationEnd={() => setAnimating(false)}
    >
      {children}
    </div>
  );
}
